import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker, Select } from "antd";
import { UserRound } from "lucide-react";
import ShiftModal from "../../../../modals/Addshift";
import Designation from "../../../../modals/addDesignation";
import Location from "../../../../modals/location";
import ModalWrapper from "../../../../Modal";
import { updateField } from "../../../../redux/slices/employee-slice";
import dayjs from "dayjs";
import AddDepartmentModal from "../../../../modal/admin/addDepartmentModal";
import { getRequest } from "../../../../service";

const Contract = () => {
  const dispatch = useDispatch();
  const form = useSelector((state) => state.employeeForm);
  const [shift, setShiftOpen] = useState(false);
  const [location, setLocationOpen] = useState(false);
  const [designation, setDesignationOpen] = useState(false);
  const [department, setDepartmentOpen] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [reportingManagers, setReportingManagers] = useState([]);

  useEffect(() => {
    // Set default status to active when component mounts
    dispatch(updateField({ name: "status", value: "active" }));

    fetchShifts();
    fetchLocations();
    fetchDesignations();
    fetchDepartments();
    fetchReportingManagers();
  }, [dispatch]);

  const fetchShifts = async () => {
    try {
      const response = await getRequest("/api/companies/shifts");
      if (response?.shifts) {
        setShifts(response.shifts);
      }
    } catch (error) {
      console.error("Error fetching shifts:", error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await getRequest("/api/companies/locations");
      if (response?.locations) {
        setLocations(response.locations);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  const fetchDesignations = async () => {
    try {
      const response = await getRequest("/api/companies/designation");
      if (response?.designations) {
        setDesignations(response.designations);
      }
    } catch (error) {
      console.error("Error fetching designations:", error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await getRequest("/api/department");
      if (response?.departments) {
        setDepartments(response.departments);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  const fetchReportingManagers = async () => {
    try {
      const response = await getRequest("/api/companies/reportto");
      if (response?.employees) {
        setReportingManagers(response.employees);
      }
    } catch (error) {
      console.error("Error fetching reporting managers:", error);
    }
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;

    if (name === "probation_months" && form.joining_date) {
      const joiningDate = dayjs(form.joining_date);
      const confirmationDate = joiningDate.add(parseInt(value), 'month').format('YYYY-MM-DD');
      dispatch(updateField({ name: "p_confirmation_date", value: confirmationDate }));
    }

    dispatch(
      updateField({ name, value: type === "checkbox" ? checked : value })
    );
  };

  const handleSelectChange = (value, name) => {
    dispatch(updateField({ name, value }));
  };

  // Helper to format time in 12-hour format with AM/PM
  const formatTime12Hour = (timeStr) => {
    if (!timeStr || timeStr === "-" || timeStr === "ss") return "-";
    const [hours, minutes] = timeStr.split(":");
    let hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${hour.toString().padStart(2, "0")}:${minutes} ${ampm}`;
  };

  const shiftOptions = shifts.map(shift => ({
    value: shift._id,
    label: `${shift.shiftName} (${formatTime12Hour(shift.startTime)} to ${formatTime12Hour(shift.endTime)})`
  }));

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" }
  ];

  const employmentTypeOptions = [
    { value: "internship", label: "Internship" },
    { value: "regular", label: "Regular" },
    { value: "contract", label: "Contract" }
  ];

  const workLocationOptions = locations.map(location => ({
    value: location._id,
    label: `${location.locationName} | ${location.city} `
  }));

  const departmentOptions = departments.map(dept => ({
    value: dept._id,
    label: dept.name
  }));

  const designationOptions = designations.map(designation => ({
    value: designation._id,
    label: designation.designationName
  }));

  const reportingToOptions = reportingManagers.map(manager => ({
    value: manager._id,
    label: `${manager.name} | ${manager.department} | ${manager.designation}`
  }));

  const handleDepartmentChange = (value) => {
    // Clear subdepartment when department changes
    dispatch(updateField({ name: "sub_department", value: "" }));

    // Find selected department and set its subdepartments
    const selectedDept = departments.find(dept => dept._id === value);
    if (selectedDept) {
      const subDeptOptions = selectedDept.subDepartments.map(subDept => ({
        value: subDept._id,
        label: subDept.name
      }));
      setSubDepartments(subDeptOptions);
    } else {
      setSubDepartments([]);
    }

    dispatch(updateField({ name: "department", value }));
  };

  const handleCloseShift = () => {
    setShiftOpen(false);
    fetchShifts();
  };
  const handleCloseDepartment = () => {
    setDepartmentOpen(false);
    fetchDepartments();
  };
  const handleCloseLocation = () => {
    setLocationOpen(false);
    fetchLocations();
  };

  const handleCloseDesignation = () => {
    setDesignationOpen(false);
    fetchDesignations();
  };

  return (
    <>
      <ModalWrapper
        open={shift}
        onRequestClose={() => setShiftOpen(false)}
        title="add shift"
      >
        <ShiftModal onClose={handleCloseShift} />
      </ModalWrapper>
      <ModalWrapper
        open={location}
        onRequestClose={() => setLocationOpen(false)}
        title="add location"
      >
        <Location onClose={handleCloseLocation} />
      </ModalWrapper>
      <ModalWrapper
        open={designation}
        onRequestClose={() => setDesignationOpen(false)}
        title="add designation"
      >
        <Designation onClose={handleCloseDesignation} />
      </ModalWrapper>

      <AddDepartmentModal
        open={department}
        onRequestClose={handleCloseDepartment}
      />

      <div className="flex items-start justify-between bg-white px-4">
        <div className="flex flex-col gap-3 mt-1">
          <UserRound className="h-7 w-7 text-active-color stroke-1" />
          <h4 className="font-bold text-md">Contract Information</h4>
          <p className="text-gray-400 text-md -mt-2">
            Fill in the employee's contract details.
          </p>
        </div>
        <div className="bg-form-bg w-lg rounded-md px-4 flex flex-col gap-6 py-8 mr-8">
          <div className="w-full grid grid-cols-12 gap-5">
            <div className="col-span-6 flex flex-col gap-6">
              <div className="flex flex-col gap-1">
                <label htmlFor="fname" className="text-md ">
                  Hire Date
                  <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
                </label>
                <DatePicker
                  onChange={(_date, dateString) =>
                    dispatch(updateField({ name: "hire_date", value: dateString }))
                  }
                  placeholder="YYYY/MM/DD"
                  value={form.hire_date ? dayjs(form.hire_date) : null}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label htmlFor="fname" className="text-md ">
                  Status
                  <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
                </label>
                <Select
                  options={statusOptions}
                  value={form.status || "active"}
                  onChange={(value) => handleSelectChange(value, "status")}
                  className="w-full"
                />
              </div>

              {form.employee_type === "contract" && (
                <div className="flex flex-col gap-1">
                  <label htmlFor="fname" className="text-md ">
                    Contract Start Date
                    <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
                  </label>
                  <DatePicker
                    onChange={(_date, dateString) =>
                      dispatch(updateField({ name: "contract_start_date", value: dateString }))
                    }
                    placeholder="YYYY/MM/DD"
                    value={form.contract_start_date ? dayjs(form.contract_start_date) : null}
                  />
                </div>
              )}


              {["regular", "internship"].includes(form.employee_type) && (
                <div className="flex flex-col gap-1">
                  <label htmlFor="probation_months" className="text-md">
                    No. of Months {form.employee_type}
                    <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
                  </label>
                  <input
                    type="number"
                    className="border px-2 py-1 rounded-md border-secondary text-md outline-none bg-white"
                    name="probation_months"
                    value={form.probation_months}
                    onChange={handleChange}
                    min="1"
                  />
                </div>
              )}
            </div>

            <div className="col-span-6 flex flex-col gap-6">
              <div className="flex flex-col gap-1">
                <label htmlFor="fname" className="text-md ">
                  Joining Date
                  <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
                </label>
                <DatePicker
                  onChange={(_date, dateString) =>
                    dispatch(updateField({ name: "joining_date", value: dateString }))
                  }
                  placeholder="YYYY/MM/DD"
                  value={form.joining_date ? dayjs(form.joining_date) : null}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label htmlFor="fname" className="text-md ">
                  Employment Type
                  <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
                </label>
                <Select
                  options={employmentTypeOptions}
                  value={form.employee_type}
                  onChange={(value) => handleSelectChange(value, "employee_type")}
                  className="w-full"
                />
              </div>

              {form.employee_type === "contract" && (
                <div className="flex flex-col gap-1 mt-0 lg:mt-0">
                  <label htmlFor="fname" className="text-md ">
                    Contract End Date
                    <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
                  </label>
                  <DatePicker
                    onChange={(_date, dateString) =>
                      dispatch(updateField({ name: "contract_end_date", value: dateString }))
                    }
                    placeholder="YYYY/MM/DD"
                    value={form.contract_end_date ? dayjs(form.contract_end_date) : null}
                  />
                </div>
              )}
            </div>
          </div>
          {/* second grid row */}
          <div className="grid grid-cols-12 gap-5">
            {/* shift */}
            <div className="flex flex-col gap-1 col-span-12">
              <div className="flex items-center justify-between">
                <label htmlFor="fname" className="text-md ">
                  Shift
                  <span className="text-red-500 relative -top-0.5 ml-0.5">
                    *
                  </span>
                </label>
                <span
                  className="text-[8px] px-2 py-0.5 bg-primary rounded-sm text-white font-semibold cursor-pointer hover:bg-active-color hover:text-white"
                  onClick={() => setShiftOpen(true)}
                >
                  Add
                </span>
              </div>
              <Select
                options={shiftOptions}
                value={form.shift}
                onChange={(value) => handleSelectChange(value, "shift")}
                className="w-full"
                placeholder="Select a shift"
              />
            </div>
            {/* confirmation date */}
            <div className="flex flex-col gap-1 col-span-12">
              <label htmlFor="fname" className="text-md ">
                Parmanent Confirmation Date
                <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
              </label>
              <DatePicker
                onChange={(_date, dateString) =>
                  dispatch(
                    updateField({
                      name: "p_confirmation_date",
                      value: dateString,
                    })
                  )
                }
                placeholder="YYYY/MM/DD"
                value={
                  form.p_confirmation_date
                    ? dayjs(form.p_confirmation_date)
                    : null
                }
                disabled={true}
              />
            </div>
          </div>
          <div className="grid grid-cols-12 gap-5">
            <div className="col-span-6 flex flex-col gap-6">
              {/* Work Location */}
              <div className="flex flex-col gap-1 w-full">
                <div className="flex items-center justify-between w-full">
                  <label htmlFor="work-location" className="text-md">
                    Work Location
                    <span className="text-red-500 relative -top-0.5 ml-0.5">
                      *
                    </span>
                  </label>
                  <span
                    className="text-[8px] px-2 py-0.5 bg-primary rounded-sm text-white font-semibold cursor-pointer hover:bg-active-color hover:text-white"
                    onClick={() => setLocationOpen(true)}
                  >
                    Add
                  </span>
                </div>
                <Select
                  options={workLocationOptions}
                  value={form.location}
                  onChange={(value) => handleSelectChange(value, "location")}
                  className="w-full"
                />
              </div>

              {/* Department */}
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <label htmlFor="department" className="text-md">
                    Department
                    <span className="text-red-500 relative -top-0.5 ml-0.5">
                      *
                    </span>
                  </label>
                  <span
                    className="text-[8px] px-2 py-0.5 bg-primary rounded-sm text-white font-semibold cursor-pointer hover:bg-active-color hover:text-white"
                    onClick={() => setDepartmentOpen(true)}
                  >
                    Add
                  </span>
                </div>
                <Select
                  options={departmentOptions}
                  value={form.department}
                  onChange={handleDepartmentChange}
                  className="w-full"
                />
              </div>
            </div>

            <div className="col-span-6 flex flex-col gap-6">
              {/* Designation */}
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <label htmlFor="designation" className="text-md">
                    Designation
                    <span className="text-red-500 relative -top-0.5 ml-0.5">
                      *
                    </span>
                  </label>
                  <span
                    className="text-[8px] font-semibold px-2 py-0.5 bg-primary rounded-sm text-white cursor-pointer hover:bg-active-color hover:text-white"
                    onClick={() => setDesignationOpen(true)}
                  >
                    Add
                  </span>
                </div>
                <Select
                  options={designationOptions}
                  value={form.designation}
                  onChange={(value) => handleSelectChange(value, "designation")}
                  className="w-full"
                />
              </div>
              {/* Sub Department */}
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <label htmlFor="sub-department" className="text-md">
                    Sub Department
                  </label>
                </div>
                <Select
                  options={subDepartments}
                  value={form.sub_department}
                  onChange={(value) => handleSelectChange(value, "sub_department")}
                  className="w-full"
                  disabled={!form.department}
                />
              </div>
            </div>
          </div>

          {/* Reporting To in new row with 12 columns */}
          <div className="grid grid-cols-12 gap-5">
            <div className="col-span-12 flex flex-col gap-1">
              <label htmlFor="sub-department" className="text-md">
                Reporting To 
              </label>
              <Select
                showSearch
                options={reportingToOptions}
                value={form.reporting_to}
                onChange={(value) => handleSelectChange(value, "reporting_to")}
                className="w-full"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                placeholder="Search and select reporting manager"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contract;
